import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users, Store, FileText, Receipt, TrendingUp, Coins, Activity,
  BarChart3, Shield, Crown, CheckCircle, XCircle, Clock, Trash2, Ban, Eye, ChevronDown, ImageIcon,
  ScrollText, Download, History, Plus, Heart, KeyRound, ShieldCheck
} from 'lucide-react';
import { AdminGrantsTab } from '@/components/AdminGrantsTab';
import { AdminAuditTab } from '@/components/AdminAuditTab';
import { AdminCleanupPlanTab } from '@/components/AdminCleanupPlanTab';
import { AdminUsersTab } from '@/components/AdminUsersTab';
import { RegenerateRecoveryCodes } from '@/components/AdminMfaGate';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { GALLERY_CATEGORIES, CATEGORY_META, buildImgUrl } from '@/components/Gallery';
import { useLang } from '@/contexts/LanguageContext';
import { BUSINESS_CATEGORIES } from '@/lib/categories';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, checkAuth } = useAuth();
  const can = (p) => (user?.admin_permissions || []).includes(p);
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [coins, setCoins] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Filters
  const [reviewStatusFilter, setReviewStatusFilter] = useState('all');
  const [receiptStatusFilter, setReceiptStatusFilter] = useState('all');

  // User txn dialog
  const [viewTxnUser, setViewTxnUser] = useState(null);
  const [userTxns, setUserTxns] = useState([]);
  // Restaurant detail modal
  const [detailRestaurantId, setDetailRestaurantId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailForm, setDetailForm] = useState({});
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailTab, setDetailTab] = useState('data');
  const [galFilter, setGalFilter] = useState('ALL');
  // PDF generation modal
  const [pdfRestaurant, setPdfRestaurant] = useState(null);
  const [pdfStart, setPdfStart] = useState('');
  const [pdfEnd, setPdfEnd] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Receipt image dialog
  const [viewImage, setViewImage] = useState(null);
  // Contracts
  const [contracts, setContracts] = useState([]);
  const [contractsLoaded, setContractsLoaded] = useState(false);
  const [contractSubtab, setContractSubtab] = useState('list'); // 'list' | 'templates'
  const [templates, setTemplates] = useState([]);
  const [viewContractId, setViewContractId] = useState(null);
  const [viewContractData, setViewContractData] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [showTemplateDlg, setShowTemplateDlg] = useState(false);
  const [tplForm, setTplForm] = useState({ version: '', title: '', content: '', activate: true });
  const [tplSaving, setTplSaving] = useState(false);
  const [kpi, setKpi] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, restaurantsRes, activityRes, kpiRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/restaurants`, { withCredentials: true }),
        axios.get(`${API}/admin/activity`, { withCredentials: true }),
        axios.get(`${API}/admin/kpi`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setRestaurants(restaurantsRes.data);
      setActivity(activityRes.data);
      setKpi(kpiRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Accesso admin richiesto', { id: 'admin-403' });
        navigate('/dashboard');
        return;
      }
    } finally { setLoading(false); }
  }, [navigate]);

  const fetchReviews = useCallback(async () => {
    const params = new URLSearchParams();
    if (reviewStatusFilter !== 'all') params.set('status', reviewStatusFilter);
    const res = await axios.get(`${API}/admin/reviews?${params}`, { withCredentials: true });
    setReviews(res.data);
  }, [reviewStatusFilter]);

  const fetchCoins = useCallback(async () => {
    const res = await axios.get(`${API}/admin/coins`, { withCredentials: true });
    setCoins(res.data);
  }, []);

  const fetchReceipts = useCallback(async () => {
    const params = new URLSearchParams();
    if (receiptStatusFilter !== 'all') params.set('status', receiptStatusFilter);
    const res = await axios.get(`${API}/admin/receipts?${params}`, { withCredentials: true });
    setReceipts(res.data);
  }, [receiptStatusFilter]);

  const fetchContracts = useCallback(async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        axios.get(`${API}/admin/contracts`, { withCredentials: true }),
        axios.get(`${API}/admin/contracts/templates/list`, { withCredentials: true }),
      ]);
      setContracts(cRes.data.items || []);
      setTemplates(tRes.data.items || []);
      setContractsLoaded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento contratti');
    }
  }, []);

  const openContractDetail = async (id) => {
    setViewContractId(id);
    try {
      const [cRes, aRes] = await Promise.all([
        axios.get(`${API}/admin/contracts/${id}`, { withCredentials: true }),
        axios.get(`${API}/admin/contracts/${id}/audit`, { withCredentials: true }),
      ]);
      setViewContractData(cRes.data);
      setAuditEvents(aRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
      setViewContractId(null);
    }
  };

  const downloadContractPdf = async (acceptance_id, number) => {
    try {
      const res = await axios.get(`${API}/contracts/acceptances/${acceptance_id}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `WOMIO_${number || acceptance_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF non disponibile'); }
  };

  const saveTemplate = async () => {
    if (!tplForm.version.trim() || !tplForm.title.trim() || !tplForm.content.trim()) {
      toast.error('Tutti i campi sono obbligatori'); return;
    }
    setTplSaving(true);
    try {
      await axios.post(`${API}/admin/contracts/templates`, tplForm, { withCredentials: true });
      toast.success('Template salvato');
      setShowTemplateDlg(false);
      setTplForm({ version: '', title: '', content: '', activate: true });
      await fetchContracts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio template');
    } finally { setTplSaving(false); }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (activeTab === 'reviews') fetchReviews(); }, [activeTab, fetchReviews]);
  useEffect(() => { if (activeTab === 'coins') fetchCoins(); }, [activeTab, fetchCoins]);
  useEffect(() => { if (activeTab === 'receipts') fetchReceipts(); }, [activeTab, fetchReceipts]);
  useEffect(() => { if (activeTab === 'contracts' && !contractsLoaded) fetchContracts(); }, [activeTab, contractsLoaded, fetchContracts]);

  // Load restaurant details when modal opens
  useEffect(() => {
    if (!detailRestaurantId) { setDetailData(null); setDetailForm({}); setDetailTab('data'); setGalFilter('ALL'); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/business/${detailRestaurantId}`, { withCredentials: true });
        setDetailData(res.data);
        setDetailForm({ ...res.data.profile });
      } catch (err) {
        console.error(err);
        toast.error('Errore caricamento dettagli');
        setDetailRestaurantId(null);
      }
    })();
  }, [detailRestaurantId]);

  const refreshDetailData = async () => {
    if (!detailRestaurantId) return;
    try {
      const res = await axios.get(`${API}/admin/business/${detailRestaurantId}`, { withCredentials: true });
      setDetailData(res.data);
    } catch (err) { console.error(err); }
  };

  const adminDeleteGalleryImage = async (image_id) => {
    if (!window.confirm(t('admin_gal_confirm_del'))) return;
    try {
      await axios.delete(`${API}/admin/business/${detailRestaurantId}/gallery/${image_id}`, { withCredentials: true });
      toast.success(t('admin_gal_deleted'));
      await refreshDetailData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin_gal_del_err'));
    }
  };

  const saveDetail = async () => {
    setDetailSaving(true);
    try {
      const payload = { ...detailForm };
      delete payload.profile_id; delete payload.user_id; delete payload.iban_masked; delete payload.created_at; delete payload.updated_at; delete payload.is_completed;
      if (!can('business.iban.edit') || detailData?.profile?.iban_masked || payload.iban === detailData?.profile?.iban) delete payload.iban;
      await axios.patch(`${API}/admin/business/${detailRestaurantId}`, payload, { withCredentials: true });
      toast.success('Dati attività aggiornati');
      setDetailRestaurantId(null);
      // Refresh list
      const restaurantsRes = await axios.get(`${API}/admin/restaurants`, { withCredentials: true });
      setRestaurants(restaurantsRes.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore salvataggio');
    } finally { setDetailSaving(false); }
  };

  const downloadPdf = async () => {
    if (!pdfStart || !pdfEnd) { toast.error('Inserisci data inizio e fine'); return; }
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/admin/business/${pdfRestaurant.restaurant_id}/invoice-pdf`, {
        params: { start_date: pdfStart, end_date: pdfEnd },
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `womio_report_${pdfRestaurant.name.replace(/\s/g, '_')}_${pdfStart}_${pdfEnd}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setPdfRestaurant(null);
      toast.success('Report generato');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore generazione PDF');
    } finally { setPdfLoading(false); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`Ruolo aggiornato a ${newRole}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deactivateUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/deactivate`, {}, { withCredentials: true });
      toast.success('Utente bloccato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const activateUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/activate`, {}, { withCredentials: true });
      toast.success('Utente sbloccato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review eliminata');
      fetchReviews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const invalidateReview = async (reviewId) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/invalidate`, {}, { withCredentials: true });
      toast.success('Review segnata come invalida');
      fetchReviews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const viewUserTxns = async (userId, userName) => {
    const res = await axios.get(`${API}/admin/coins/${userId}/transactions`, { withCredentials: true });
    setUserTxns(res.data);
    setViewTxnUser(userName);
  };

  const viewReceiptImage = async (imageUrl) => {
    try {
      const res = await axios.get(`${API}/files/${imageUrl}`, { withCredentials: true, responseType: 'blob' });
      setViewImage(URL.createObjectURL(res.data));
    } catch { toast.error('Immagine non disponibile'); }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;
  if (!stats) return null;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <Badge className="bg-primary/10 text-primary border-primary/20 text-xs font-sans mb-2"><Shield className="w-3 h-3 mr-1" strokeWidth={1.5} /> Admin</Badge>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Pannello Admin</h1>
            <p className="text-muted-foreground text-sm mt-1 font-sans">Gestione e monitoraggio piattaforma Womio</p>
          </div>
          <div className="flex gap-2 flex-wrap">
          <RegenerateRecoveryCodes />
          <Button
            variant="outline"
            onClick={() => navigate('/admin/social-projects')}
            className="rounded-full btn-lift"
            data-testid="admin-cta-social-projects"
          >
            <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_admin_social_projects')}
          </Button>
          </div>
        </div>

        {/* KPI Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Utenti', value: stats.overview.total_users, icon: <Users className="w-4 h-4" strokeWidth={1.5} /> },
            { label: 'Ristoranti', value: stats.overview.total_restaurants, icon: <Store className="w-4 h-4" strokeWidth={1.5} /> },
            { label: 'Review', value: stats.overview.total_reviews, icon: <FileText className="w-4 h-4" strokeWidth={1.5} /> },
            { label: 'Revenue', value: `€${stats.financial.total_volume}`, icon: <TrendingUp className="w-4 h-4" strokeWidth={1.5} /> },
            { label: 'Platform (3%)', value: `€${stats.financial.platform_revenue_3pct}`, icon: <Coins className="w-4 h-4" strokeWidth={1.5} /> },
          ].map((k, i) => (
            <Card key={k.label} className="border border-border rounded-xl" data-testid={`admin-kpi-${i}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-primary">{k.icon}</div>
                <div><p className="text-lg font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{k.value}</p><p className="text-[10px] text-muted-foreground font-sans">{k.label}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-muted rounded-xl p-1 flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="overview" className="rounded-lg font-sans text-xs sm:text-sm"><BarChart3 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Overview</TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg font-sans text-xs sm:text-sm" data-testid="admin-tab-users"><Users className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Utenti</TabsTrigger>
            <TabsTrigger value="restaurants" className="rounded-lg font-sans text-xs sm:text-sm"><Store className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Ristoranti</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg font-sans text-xs sm:text-sm"><FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Review</TabsTrigger>
            <TabsTrigger value="coins" className="rounded-lg font-sans text-xs sm:text-sm"><Coins className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Coin</TabsTrigger>
            <TabsTrigger value="receipts" className="rounded-lg font-sans text-xs sm:text-sm"><Receipt className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Validazioni</TabsTrigger>
            <TabsTrigger value="contracts" className="rounded-lg font-sans text-xs sm:text-sm" data-testid="admin-tab-contracts"><ScrollText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> {t('ct_admin_tab')}</TabsTrigger>
            {can('admin.grants.manage') && <TabsTrigger value="grants" className="rounded-lg font-sans text-xs sm:text-sm" data-testid="admin-tab-grants"><KeyRound className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Accessi Admin</TabsTrigger>}
            {can('audit.read') && <TabsTrigger value="audit" className="rounded-lg font-sans text-xs sm:text-sm" data-testid="admin-tab-audit"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Registro</TabsTrigger>}
            {can('admin.grants.manage') && <TabsTrigger value="cleanup" className="rounded-lg font-sans text-xs sm:text-sm" data-testid="admin-tab-cleanup"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Piano pulizia</TabsTrigger>}
          </TabsList>
          {can('admin.grants.manage') && <TabsContent value="grants"><AdminGrantsTab /></TabsContent>}
          {can('audit.read') && <TabsContent value="audit"><AdminAuditTab /></TabsContent>}
          {can('admin.grants.manage') && <TabsContent value="cleanup"><AdminCleanupPlanTab /></TabsContent>}

          {/* OVERVIEW */}
          <TabsContent value="overview">
            {/* === KPI CHARTS (nuovo modello 1%/4%/3%) === */}
            {kpi && (
              <div className="mb-6 space-y-4" data-testid="admin-kpi-section">
                {/* Row 1: Reward breakdown cards + Coin lifecycle bar */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Card className="border border-border rounded-xl" data-testid="kpi-reward-author">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Users className="w-4 h-4 text-blue-700" strokeWidth={1.5} /></div>
                        <p className="text-xs text-muted-foreground font-sans">Reward Autore (1%)</p>
                      </div>
                      <p className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>€{kpi.reward_breakdown.author.total}</p>
                      <p className="text-[11px] text-muted-foreground font-sans">{kpi.reward_breakdown.author.count} distribuzioni</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-border rounded-xl" data-testid="kpi-reward-referrer">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-emerald-700" strokeWidth={1.5} /></div>
                        <p className="text-xs text-muted-foreground font-sans">Reward Referrer (4%)</p>
                      </div>
                      <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>€{kpi.reward_breakdown.referrer.total}</p>
                      <p className="text-[11px] text-muted-foreground font-sans">{kpi.reward_breakdown.referrer.count} distribuzioni</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-primary/30 rounded-xl bg-gradient-to-br from-primary/5 to-transparent" data-testid="kpi-reward-a2s">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center"><Heart className="w-4 h-4 text-rose-600" strokeWidth={1.5} /></div>
                        <p className="text-xs text-muted-foreground font-sans">Action2Smile (3%)</p>
                      </div>
                      <p className="text-2xl font-bold text-rose-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>€{kpi.reward_breakdown.action2smile.total}</p>
                      <p className="text-[11px] text-muted-foreground font-sans">{kpi.reward_breakdown.action2smile.count} donazioni</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Row 2: Line chart trend + Pie coin lifecycle */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border border-border rounded-xl" data-testid="kpi-monthly-chart">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-sans font-semibold flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-primary" strokeWidth={1.5} /> Andamento reward (ultimi 6 mesi)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={kpi.monthly_series} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Line type="monotone" dataKey="author" name="Autore 1%" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="referrer" name="Referrer 4%" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="action2smile" name="A2S 3%" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="border border-border rounded-xl" data-testid="kpi-coin-lifecycle">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-sans font-semibold flex items-center gap-2">
                        <Coins className="w-4 h-4 text-amber-600" strokeWidth={1.5} /> Ciclo di vita coin
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={[
                          { name: 'Generati', value: kpi.coins_lifecycle.generated, fill: '#10b981' },
                          { name: 'Utilizzati', value: kpi.coins_lifecycle.used, fill: '#f59e0b' },
                          { name: 'Scaduti', value: kpi.coins_lifecycle.expired, fill: '#9ca3af' },
                          { name: 'Wallet attivo', value: kpi.coins_lifecycle.active_balance, fill: '#0ea5e9' },
                        ]} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => `${v} coin`} />
                          <Bar dataKey="value" name="Coin">
                            {[{ fill: '#10b981' }, { fill: '#f59e0b' }, { fill: '#9ca3af' }, { fill: '#0ea5e9' }].map((c, i) => <Cell key={i} fill={c.fill} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="flex justify-between text-[10px] text-muted-foreground font-sans mt-2 border-t border-border pt-2">
                        <span>v1 legacy: €{kpi.coins_lifecycle.generated_v1_legacy}</span>
                        <span>v2 corrente: €{kpi.coins_lifecycle.generated_v2_current}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Row 3: Reward split pie + Top referrers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border border-border rounded-xl" data-testid="kpi-reward-pie">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-sans font-semibold flex items-center gap-2">
                        <Activity className="w-4 h-4 text-primary" strokeWidth={1.5} /> Ripartizione reward (totale)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Autore 1%', value: kpi.reward_breakdown.author.total, fill: '#2563eb' },
                              { name: 'Referrer 4%', value: kpi.reward_breakdown.referrer.total, fill: '#059669' },
                              { name: 'Action2Smile 3%', value: kpi.reward_breakdown.action2smile.total, fill: '#e11d48' },
                            ]}
                            cx="50%" cy="50%" innerRadius={45} outerRadius={80}
                            dataKey="value" nameKey="name"
                            label={({ name, value }) => value > 0 ? `${name}: €${value}` : ''}
                            labelLine={false}
                          />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => `€${v}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="border border-border rounded-xl" data-testid="kpi-top-referrers">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-sans font-semibold flex items-center gap-2">
                        <Crown className="w-4 h-4 text-emerald-600" strokeWidth={1.5} /> Top Referrer (chi porta più clienti)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {kpi.top_referrers.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-6 text-center font-sans">Nessun referrer ancora</p>
                      ) : (
                        <div className="space-y-1.5">
                          {kpi.top_referrers.map((r, i) => (
                            <div key={r.user_id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                                <span className="text-sm font-sans truncate">{r.name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge className="bg-muted text-muted-foreground text-[10px]">{r.count}x</Badge>
                                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-mono">€{r.earned}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Legacy receipts cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="border border-border rounded-xl"><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-700" strokeWidth={1.5} /></div><div><p className="text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stats.overview.approved_receipts}</p><p className="text-xs text-muted-foreground font-sans">Approvate</p></div></CardContent></Card>
              <Card className="border border-border rounded-xl"><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center"><XCircle className="w-5 h-5 text-destructive" strokeWidth={1.5} /></div><div><p className="text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stats.overview.rejected_receipts}</p><p className="text-xs text-muted-foreground font-sans">Rifiutate</p></div></CardContent></Card>
              <Card className="border border-border rounded-xl"><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-700" strokeWidth={1.5} /></div><div><p className="text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{stats.overview.pending_receipts}</p><p className="text-xs text-muted-foreground font-sans">In attesa</p></div></CardContent></Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border border-border rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm font-sans font-semibold flex items-center gap-2"><Crown className="w-4 h-4 text-amber-600" /> Top Ristoranti</CardTitle></CardHeader><CardContent>{stats.top_restaurants.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center font-sans">Nessun dato</p> : <div className="space-y-2">{stats.top_restaurants.map((r,i) => <div key={r.restaurant_id || r.name || i} className="flex items-center justify-between py-1 border-b border-border last:border-0"><span className="text-sm font-sans">{r.name}</span><span className="text-sm font-bold text-emerald-700">€{r.revenue}</span></div>)}</div>}</CardContent></Card>
              <Card className="border border-border rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm font-sans font-semibold flex items-center gap-2"><Coins className="w-4 h-4 text-emerald-600" /> Top Earners</CardTitle></CardHeader><CardContent>{stats.top_earners.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center font-sans">Nessun dato</p> : <div className="space-y-2">{stats.top_earners.map((e,i) => <div key={e.user_id || e.name || i} className="flex items-center justify-between py-1 border-b border-border last:border-0"><span className="text-sm font-sans">{e.name}</span><Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{e.earned} coin</Badge></div>)}</div>}</CardContent></Card>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users">
            <AdminUsersTab canGrant={can('admin.grants.manage')} onChangeRole={changeRole} onDeactivate={deactivateUser} onActivate={activateUser} />
          </TabsContent>

          {/* RESTAURANTS */}
          <TabsContent value="restaurants">
            <Card className="border border-border rounded-xl"><CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="font-sans text-xs">Nome</TableHead>
                  <TableHead className="font-sans text-xs">Indirizzo</TableHead>
                  <TableHead className="font-sans text-xs">Owner</TableHead>
                  <TableHead className="font-sans text-xs">Email</TableHead>
                  <TableHead className="font-sans text-xs">Revenue</TableHead>
                  <TableHead className="font-sans text-xs">Fee (3%)</TableHead>
                  <TableHead className="font-sans text-xs">Clienti</TableHead>
                  <TableHead className="font-sans text-xs text-right">Report</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {restaurants.map((r, i) => (
                    <TableRow key={r.restaurant_id} data-testid={`admin-rest-${i}`} className="cursor-pointer hover:bg-muted/30" onClick={() => setDetailRestaurantId(r.restaurant_id)}>
                      <TableCell className="text-sm font-sans font-semibold">{r.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground font-sans">{r.address}</TableCell>
                      <TableCell className="text-xs font-sans">{r.owner_name}</TableCell>
                      <TableCell className="text-xs font-sans text-muted-foreground" data-testid={`admin-rest-email-${i}`}>{r.owner_email || '—'}</TableCell>
                      <TableCell className="text-sm font-bold text-emerald-700 font-sans">€{r.total_revenue}</TableCell>
                      <TableCell className="text-xs font-sans">€{(r.total_revenue * 0.03).toFixed(2)}</TableCell>
                      <TableCell className="text-xs font-sans">{r.customer_count}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setPdfRestaurant(r); }} data-testid={`admin-rest-pdf-${i}`}>
                          <FileText className="w-4 h-4 text-primary" strokeWidth={1.5} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews">
            <div className="flex items-center gap-3 mb-4">
              <Select value={reviewStatusFilter} onValueChange={setReviewStatusFilter}>
                <SelectTrigger className="w-40 h-9 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="SAVED">SAVED</SelectItem>
                  <SelectItem value="IN_VALIDATION">IN_VALIDATION</SelectItem>
                  <SelectItem value="VALIDATED">VALIDATED</SelectItem>
                  <SelectItem value="EXPIRED">EXPIRED</SelectItem>
                  <SelectItem value="INVALID">INVALID</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground font-sans">{reviews.length} review</span>
            </div>
            <Card className="border border-border rounded-xl"><CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="font-sans text-xs">Utente</TableHead>
                  <TableHead className="font-sans text-xs">Ristorante</TableHead>
                  <TableHead className="font-sans text-xs">Stato</TableHead>
                  <TableHead className="font-sans text-xs">Rating</TableHead>
                  <TableHead className="font-sans text-xs">Importo</TableHead>
                  <TableHead className="font-sans text-xs">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {reviews.map((rv, i) => (
                    <TableRow key={rv.review_id} data-testid={`admin-review-${i}`}>
                      <TableCell className="text-xs font-sans">{rv.user_name}</TableCell>
                      <TableCell className="text-xs font-sans">{rv.restaurant_name}</TableCell>
                      <TableCell><Badge className={`text-[10px] ${rv.status==='VALIDATED'?'bg-emerald-50 text-emerald-700':rv.status==='IN_VALIDATION'?'bg-blue-50 text-blue-700':rv.status==='SAVED'?'bg-amber-50 text-amber-700':'bg-muted text-muted-foreground'}`}>{rv.status}</Badge></TableCell>
                      <TableCell className="text-xs font-sans">{rv.rating || '-'}</TableCell>
                      <TableCell className="text-xs font-sans">{rv.receipt_amount ? `€${rv.receipt_amount}` : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => invalidateReview(rv.review_id)} className="h-7 w-7 p-0 text-amber-600" title="Segna invalida"><Ban className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteReview(rv.review_id)} className="h-7 w-7 p-0 text-destructive" title="Elimina"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* COINS */}
          <TabsContent value="coins">
            {coins && (
              <>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card className="border border-border rounded-xl"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{coins.total_generated}</p><p className="text-xs text-muted-foreground font-sans">Generati</p></CardContent></Card>
                  <Card className="border border-border rounded-xl"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-primary" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{coins.total_spent}</p><p className="text-xs text-muted-foreground font-sans">Spesi</p></CardContent></Card>
                  <Card className="border border-border rounded-xl"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-muted-foreground" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{coins.total_expired}</p><p className="text-xs text-muted-foreground font-sans">Scaduti</p></CardContent></Card>
                </div>
                <Card className="border border-border rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm font-sans font-semibold">Wallet per utente</CardTitle></CardHeader><CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="font-sans text-xs">Utente</TableHead>
                      <TableHead className="font-sans text-xs">Email</TableHead>
                      <TableHead className="font-sans text-xs">Saldo</TableHead>
                      <TableHead className="font-sans text-xs">Azioni</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {coins.wallets.map((w, i) => (
                        <TableRow key={w.user_id} data-testid={`admin-wallet-${i}`}>
                          <TableCell className="text-xs font-sans">{w.user_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground font-sans">{w.user_email}</TableCell>
                          <TableCell className="text-sm font-bold font-sans">{w.balance?.toFixed(2)}</TableCell>
                          <TableCell><Button variant="outline" size="sm" onClick={() => viewUserTxns(w.user_id, w.user_name)} className="h-7 text-[10px] rounded"><Activity className="w-3 h-3 mr-1" /> Storico</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent></Card>
              </>
            )}
          </TabsContent>

          {/* RECEIPTS / VALIDATIONS */}
          <TabsContent value="receipts">
            <div className="flex items-center gap-3 mb-4">
              <Select value={receiptStatusFilter} onValueChange={setReceiptStatusFilter}>
                <SelectTrigger className="w-40 h-9 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground font-sans">{receipts.length} scontrini</span>
            </div>
            <Card className="border border-border rounded-xl"><CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="font-sans text-xs">Utente</TableHead>
                  <TableHead className="font-sans text-xs">Ristorante</TableHead>
                  <TableHead className="font-sans text-xs">Importo</TableHead>
                  <TableHead className="font-sans text-xs">Stato</TableHead>
                  <TableHead className="font-sans text-xs">Foto</TableHead>
                  <TableHead className="font-sans text-xs">Data</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {receipts.map((rc, i) => (
                    <TableRow key={rc.receipt_id || i} data-testid={`admin-receipt-${i}`}>
                      <TableCell className="text-xs font-sans">{rc.user_name}</TableCell>
                      <TableCell className="text-xs font-sans">{rc.restaurant_name}</TableCell>
                      <TableCell className="text-sm font-bold font-sans">€{rc.amount}</TableCell>
                      <TableCell><Badge className={`text-[10px] ${rc.status==='approved'?'bg-emerald-50 text-emerald-700':rc.status==='rejected'?'bg-destructive/10 text-destructive':'bg-amber-50 text-amber-700'}`}>{rc.status}</Badge></TableCell>
                      <TableCell>{rc.image_url ? <Button variant="ghost" size="sm" onClick={() => viewReceiptImage(rc.image_url)} className="h-7 w-7 p-0"><Eye className="w-3 h-3" /></Button> : '-'}</TableCell>
                      <TableCell className="text-[10px] text-muted-foreground font-sans">{new Date(rc.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          {/* CONTRACTS */}
          <TabsContent value="contracts">
            <div className="flex items-center gap-3 mb-4">
              <Select value={contractSubtab} onValueChange={setContractSubtab}>
                <SelectTrigger className="w-56 h-9 rounded-lg text-sm" data-testid="admin-contracts-subtab">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="list">{t('ct_admin_tab')} ({contracts.length})</SelectItem>
                  <SelectItem value="templates">{t('ct_admin_template_tab')} ({templates.length})</SelectItem>
                </SelectContent>
              </Select>
              {contractSubtab === 'templates' && can('contracts.templates.manage') && (
                <Button size="sm" onClick={() => setShowTemplateDlg(true)} className="rounded-full" data-testid="admin-tpl-new-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> {t('ct_admin_upload_new')}
                </Button>
              )}
            </div>

            {contractSubtab === 'list' ? (
              <Card className="border border-border rounded-xl"><CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="font-sans text-xs">{t('ct_admin_number')}</TableHead>
                    <TableHead className="font-sans text-xs">{t('ct_admin_business')}</TableHead>
                    <TableHead className="font-sans text-xs">{t('ct_admin_vat')}</TableHead>
                    <TableHead className="font-sans text-xs">{t('ct_admin_legal_rep')}</TableHead>
                    <TableHead className="font-sans text-xs">{t('ct_admin_version')}</TableHead>
                    <TableHead className="font-sans text-xs">{t('ct_admin_status')}</TableHead>
                    <TableHead className="font-sans text-xs">{t('ct_admin_signed_at')}</TableHead>
                    <TableHead className="font-sans text-xs text-right">Azioni</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {contracts.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground font-sans text-sm py-8" data-testid="admin-contracts-empty">{t('ct_admin_no_contracts')}</TableCell></TableRow>
                    ) : contracts.map((c, i) => (
                      <TableRow key={c.acceptance_id} data-testid={`admin-contract-row-${i}`} className="cursor-pointer hover:bg-muted/30" onClick={() => openContractDetail(c.acceptance_id)}>
                        <TableCell className="text-xs font-mono font-semibold">{c.contract_number}</TableCell>
                        <TableCell className="text-sm font-sans">{c.snapshot_data?.company_name || c.user_name || '—'}</TableCell>
                        <TableCell className="text-xs font-sans">{c.snapshot_data?.vat_number || '—'}</TableCell>
                        <TableCell className="text-xs font-sans">{`${c.snapshot_data?.legal_rep_first_name || ''} ${c.snapshot_data?.legal_rep_last_name || ''}`.trim() || '—'}</TableCell>
                        <TableCell className="text-xs font-sans">{c.template_version}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${c.status === 'signed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : c.status === 'otp_pending' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-muted text-muted-foreground'}`}>{c.status}</Badge>
                        </TableCell>
                        <TableCell className="text-[10px] text-muted-foreground font-sans">{c.signed_at ? new Date(c.signed_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-right">
                          {c.status === 'signed' && (
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); downloadContractPdf(c.acceptance_id, c.contract_number); }} data-testid={`admin-contract-pdf-${i}`}>
                              <Download className="w-4 h-4 text-primary" strokeWidth={1.5} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent></Card>
            ) : (
              <Card className="border border-border rounded-xl"><CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="font-sans text-xs">{t('ct_admin_tpl_version')}</TableHead>
                    <TableHead className="font-sans text-xs">{t('ct_admin_tpl_title')}</TableHead>
                    <TableHead className="font-sans text-xs">Attivo</TableHead>
                    <TableHead className="font-sans text-xs">Checksum</TableHead>
                    <TableHead className="font-sans text-xs">Creato</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {templates.map((tpl, i) => (
                      <TableRow key={tpl.template_id} data-testid={`admin-tpl-row-${i}`}>
                        <TableCell className="text-sm font-mono font-semibold">{tpl.version}</TableCell>
                        <TableCell className="text-xs font-sans">{tpl.title}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${tpl.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>{tpl.is_active ? 'Attivo' : 'Archiviato'}</Badge>
                        </TableCell>
                        <TableCell className="text-[10px] font-mono text-muted-foreground">{(tpl.checksum_sha256 || '').slice(0, 16)}...</TableCell>
                        <TableCell className="text-[10px] text-muted-foreground font-sans">{new Date(tpl.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* User Transactions Dialog */}
      <Dialog open={!!viewTxnUser} onOpenChange={() => setViewTxnUser(null)}>
        <DialogContent className="max-w-lg rounded-2xl mx-4"><DialogHeader><DialogTitle className="font-sans">Transazioni: {viewTxnUser}</DialogTitle><DialogDescription className="font-sans">Storico movimenti coin</DialogDescription></DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {userTxns.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4 font-sans">Nessuna transazione</p> : (
              <Table><TableBody>{userTxns.map((t,i) => (
                <TableRow key={t.transaction_id || i}><TableCell className="text-xs font-sans">{new Date(t.created_at).toLocaleDateString()}</TableCell><TableCell className="text-xs font-sans">{t.description}</TableCell><TableCell className={`text-xs font-bold ${t.type==='earn'?'text-emerald-700':'text-primary'}`}>{t.type==='earn'?'+':'-'}{t.amount}</TableCell></TableRow>
              ))}</TableBody></Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Image Dialog */}
      <Dialog open={!!viewImage} onOpenChange={() => { if(viewImage) URL.revokeObjectURL(viewImage); setViewImage(null); }}>
        <DialogContent className="max-w-2xl rounded-2xl mx-4"><DialogHeader><DialogTitle className="font-sans">Scontrino</DialogTitle></DialogHeader>
          {viewImage && <img src={viewImage} alt="Receipt" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>

      {/* Restaurant Detail Edit Dialog */}
      <Dialog open={!!detailRestaurantId} onOpenChange={() => setDetailRestaurantId(null)}>
        <DialogContent className="max-w-2xl rounded-2xl mx-4 max-h-[85vh] overflow-y-auto" data-testid="admin-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans">Dettagli attività: {detailData?.restaurant?.name || '...'}</DialogTitle>
            <DialogDescription className="font-sans">Modifica dei dati di registrazione.{can('business.iban.edit') ? ' Tutti i campi sono editabili.' : ' IBAN modificabile solo da admin_super.'}</DialogDescription>
          </DialogHeader>
          {!detailData ? <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div> : (
            <Tabs value={detailTab} onValueChange={setDetailTab} className="pt-1">
              <TabsList className="bg-muted rounded-xl p-1 mb-3" data-testid="admin-detail-tabs">
                <TabsTrigger value="data" className="rounded-lg font-sans text-xs sm:text-sm" data-testid="admin-detail-tab-data">
                  <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Dati
                </TabsTrigger>
                <TabsTrigger value="gallery" className="rounded-lg font-sans text-xs sm:text-sm" data-testid="admin-detail-tab-gallery">
                  <ImageIcon className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> {t('admin_gal_tab')}
                  <span className="ml-1.5 text-[10px] text-muted-foreground">({(detailData.restaurant?.gallery || []).length})</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="data">
                <div className="space-y-3 py-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs font-sans">{t('bn_label')}</Label><Input value={detailForm.business_name || ''} onChange={e => setDetailForm({...detailForm, business_name: e.target.value})} className="h-9 text-sm" data-testid="detail-business-name" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Ragione sociale</Label><Input value={detailForm.company_name || ''} onChange={e => setDetailForm({...detailForm, company_name: e.target.value})} className="h-9 text-sm" data-testid="detail-company-name" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Indirizzo</Label><Input value={detailForm.registered_address || ''} onChange={e => setDetailForm({...detailForm, registered_address: e.target.value})} className="h-9 text-sm" data-testid="detail-address" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Partita IVA</Label><Input value={detailForm.vat_number || ''} onChange={e => setDetailForm({...detailForm, vat_number: e.target.value})} className="h-9 text-sm" data-testid="detail-vat" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">IBAN</Label><Input disabled={!can('business.iban.edit')} title={can('business.iban.edit') ? '' : 'Modifica IBAN riservata ad admin_super'} value={detailForm.iban || ''} onChange={e => setDetailForm({...detailForm, iban: e.target.value.toUpperCase()})} className="h-9 text-sm" data-testid="detail-iban" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Telefono</Label><Input value={detailForm.phone_number || ''} onChange={e => setDetailForm({...detailForm, phone_number: e.target.value})} className="h-9 text-sm" data-testid="detail-phone" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Categoria</Label>
                      <Select value={detailForm.category || ''} onValueChange={v => setDetailForm({...detailForm, category: v})}>
                        <SelectTrigger className="h-9 text-sm" data-testid="detail-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{BUSINESS_CATEGORIES.map(c => <SelectItem key={c} value={c} data-testid={`detail-cat-${c}`}>{t(`cat_${c}`)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2 font-sans">Legale rappresentante</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs font-sans">Nome</Label><Input value={detailForm.legal_rep_first_name || ''} onChange={e => setDetailForm({...detailForm, legal_rep_first_name: e.target.value})} className="h-9 text-sm" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Cognome</Label><Input value={detailForm.legal_rep_last_name || ''} onChange={e => setDetailForm({...detailForm, legal_rep_last_name: e.target.value})} className="h-9 text-sm" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Luogo di nascita</Label><Input value={detailForm.legal_rep_place_of_birth || ''} onChange={e => setDetailForm({...detailForm, legal_rep_place_of_birth: e.target.value})} className="h-9 text-sm" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Data di nascita</Label><Input type="date" value={detailForm.legal_rep_date_of_birth || ''} onChange={e => setDetailForm({...detailForm, legal_rep_date_of_birth: e.target.value})} className="h-9 text-sm" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Codice fiscale</Label><Input value={detailForm.legal_rep_fiscal_code || ''} onChange={e => setDetailForm({...detailForm, legal_rep_fiscal_code: e.target.value.toUpperCase()})} maxLength={16} className="h-9 text-sm uppercase" /></div>
                    <div className="space-y-1"><Label className="text-xs font-sans">Tipo documento</Label>
                      <Select value={detailForm.legal_rep_document_type || ''} onValueChange={v => setDetailForm({...detailForm, legal_rep_document_type: v})}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="carta_identita">Carta d&apos;identità</SelectItem><SelectItem value="passaporto">Passaporto</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 sm:col-span-2"><Label className="text-xs font-sans">Numero documento</Label><Input value={detailForm.legal_rep_document_number || ''} onChange={e => setDetailForm({...detailForm, legal_rep_document_number: e.target.value})} className="h-9 text-sm" /></div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="gallery">
                <div className="space-y-3 py-2" data-testid="admin-gallery-panel">
                  <p className="text-xs text-muted-foreground font-sans">{t('admin_gal_title')}</p>
                  {/* Category filter */}
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {(() => {
                      const gal = detailData.restaurant?.gallery || [];
                      const chips = [{ key: 'ALL', label: t('gal_all'), count: gal.length, Icon: ImageIcon }];
                      GALLERY_CATEGORIES.forEach(c => chips.push({ key: c, label: t(CATEGORY_META[c].labelKey), count: gal.filter(g => g.category === c).length, Icon: CATEGORY_META[c].icon }));
                      return chips.map(({ key, label, count, Icon }) => {
                        const isActive = galFilter === key;
                        return (
                          <button key={key} onClick={() => setGalFilter(key)} data-testid={`admin-gal-chip-${key.toLowerCase()}`}
                            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-sans font-medium transition-colors border ${isActive ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:text-foreground'}`}>
                            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
                            <span className={`text-[10px] ${isActive ? 'opacity-90' : 'opacity-60'}`}>({count})</span>
                          </button>
                        );
                      });
                    })()}
                  </div>
                  {(() => {
                    const gal = detailData.restaurant?.gallery || [];
                    const filtered = (galFilter === 'ALL' ? gal : gal.filter(g => g.category === galFilter))
                      .slice().sort((a, b) => (a.category || '').localeCompare(b.category || '') || (a.order - b.order));
                    if (filtered.length === 0) {
                      return <div className="w-full py-8 border border-dashed border-border rounded-xl text-center text-muted-foreground text-sm font-sans" data-testid="admin-gallery-empty">{t('admin_gal_empty')}</div>;
                    }
                    return (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {filtered.map((img, i) => (
                          <div key={img.image_id} className="relative aspect-square rounded-xl overflow-hidden border border-border group" data-testid={`admin-gal-item-${i}`}>
                            <img src={buildImgUrl(img.url_thumb)} alt="" loading="lazy" className="w-full h-full object-cover" />
                            {img.is_cover && (
                              <div className="absolute top-1.5 left-1.5 bg-primary text-white text-[10px] font-sans px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Crown className="w-2.5 h-2.5 fill-white" strokeWidth={2} /> Cover
                              </div>
                            )}
                            <div className="absolute top-1.5 right-1.5 bg-black/60 text-white text-[9px] font-sans px-1.5 py-0.5 rounded">
                              {t(CATEGORY_META[img.category]?.labelKey || 'gal_cat_locale')}
                            </div>
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button onClick={() => adminDeleteGalleryImage(img.image_id)} className="p-2 rounded-full bg-destructive text-white hover:bg-destructive/90" title={t('gal_delete')} data-testid={`admin-gal-delete-${i}`}>
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDetailRestaurantId(null)} className="rounded-full" data-testid="detail-cancel">Annulla</Button>
            {detailTab === 'data' && (
              <Button onClick={saveDetail} disabled={detailSaving || !detailData} className="rounded-full" data-testid="detail-save">{detailSaving ? 'Salvo...' : 'Salva modifiche'}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Period Picker Dialog */}
      <Dialog open={!!pdfRestaurant} onOpenChange={() => setPdfRestaurant(null)}>
        <DialogContent className="max-w-md rounded-2xl mx-4" data-testid="admin-pdf-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans">Genera Report Fatturazione</DialogTitle>
            <DialogDescription className="font-sans">
              Report PDF con la quota Womio (3%) per <span className="font-semibold">{pdfRestaurant?.name}</span>. Indica il periodo di fatturazione.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-xs font-sans">Data inizio periodo</Label><Input type="date" value={pdfStart} onChange={e => setPdfStart(e.target.value)} className="h-10" data-testid="pdf-start-date" /></div>
            <div className="space-y-1"><Label className="text-xs font-sans">Data fine periodo</Label><Input type="date" value={pdfEnd} onChange={e => setPdfEnd(e.target.value)} className="h-10" data-testid="pdf-end-date" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPdfRestaurant(null)} className="rounded-full" data-testid="pdf-cancel">Annulla</Button>
            <Button onClick={downloadPdf} disabled={pdfLoading || !pdfStart || !pdfEnd} className="rounded-full" data-testid="pdf-generate">{pdfLoading ? 'Generazione...' : 'Scarica PDF'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Detail Dialog */}
      <Dialog open={!!viewContractId} onOpenChange={() => { setViewContractId(null); setViewContractData(null); setAuditEvents([]); }}>
        <DialogContent className="max-w-3xl rounded-2xl mx-4 max-h-[85vh] overflow-y-auto" data-testid="admin-contract-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-primary" strokeWidth={1.5} />
              {viewContractData?.contract_number || '—'}
            </DialogTitle>
            <DialogDescription className="font-sans">{viewContractData?.snapshot_data?.company_name || '—'} — v{viewContractData?.template_version || '—'}</DialogDescription>
          </DialogHeader>
          {!viewContractData ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="space-y-4 py-2 text-sm font-sans">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span className="text-muted-foreground text-xs">P.IVA</span><div className="font-mono">{viewContractData.snapshot_data?.vat_number || '—'}</div></div>
                <div><span className="text-muted-foreground text-xs">C.F.</span><div className="font-mono">{viewContractData.snapshot_data?.company_fiscal_code || '—'}</div></div>
                <div><span className="text-muted-foreground text-xs">Legale rappr.</span><div>{`${viewContractData.snapshot_data?.legal_rep_first_name || ''} ${viewContractData.snapshot_data?.legal_rep_last_name || ''}`.trim() || '—'}</div></div>
                <div><span className="text-muted-foreground text-xs">C.F. legale rappr.</span><div className="font-mono">{viewContractData.snapshot_data?.legal_rep_fiscal_code || '—'}</div></div>
                <div><span className="text-muted-foreground text-xs">Stato</span><div><Badge className={`text-[10px] ${viewContractData.status === 'signed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{viewContractData.status}</Badge></div></div>
                <div><span className="text-muted-foreground text-xs">Firma</span><div>{viewContractData.signed_at ? new Date(viewContractData.signed_at).toLocaleString() : '—'}</div></div>
                <div className="sm:col-span-2"><span className="text-muted-foreground text-xs">PDF SHA-256</span><div className="font-mono text-[10px] break-all">{viewContractData.pdf_checksum_sha256 || '—'}</div></div>
                <div className="sm:col-span-2"><span className="text-muted-foreground text-xs">Template checksum</span><div className="font-mono text-[10px] break-all">{viewContractData.template_checksum || '—'}</div></div>
              </div>

              {viewContractData.status === 'signed' && (
                <Button variant="outline" onClick={() => downloadContractPdf(viewContractData.acceptance_id, viewContractData.contract_number)} className="rounded-full" data-testid="admin-contract-download-btn">
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('ct_admin_pdf')}
                </Button>
              )}

              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><History className="w-4 h-4 text-primary" strokeWidth={1.5} /> {t('ct_admin_audit')}</h4>
                <div className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-y-auto" data-testid="admin-audit-list">
                  {auditEvents.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-xs">Nessun evento</div>
                  ) : auditEvents.map((ev, i) => (
                    <div key={ev.event_id} className="p-3 text-xs" data-testid={`admin-audit-event-${i}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="font-mono font-semibold text-primary">{ev.event_type}</span>
                        <span className="text-muted-foreground text-[10px]">{new Date(ev.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="text-muted-foreground text-[10px]">IP: {ev.ip_address || '—'} · UA: {(ev.user_agent || '—').slice(0, 60)}</div>
                      {ev.metadata && Object.keys(ev.metadata).length > 0 && (
                        <div className="text-[10px] mt-1 font-mono bg-muted/40 p-1.5 rounded">{JSON.stringify(ev.metadata)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Upload Dialog */}
      <Dialog open={showTemplateDlg} onOpenChange={setShowTemplateDlg}>
        <DialogContent className="max-w-2xl rounded-2xl mx-4 max-h-[85vh] overflow-y-auto" data-testid="admin-tpl-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans">{t('ct_admin_upload_new')}</DialogTitle>
            <DialogDescription className="font-sans">Carica una nuova versione del contratto. La versione precedente resta immutabile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs font-sans">{t('ct_admin_tpl_version')}</Label>
              <Input placeholder="es. 1.1.0" value={tplForm.version} onChange={e => setTplForm({...tplForm, version: e.target.value})} className="h-9 text-sm" data-testid="admin-tpl-version" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-sans">{t('ct_admin_tpl_title')}</Label>
              <Input placeholder="Contratto di Adesione alla piattaforma WOMIO" value={tplForm.title} onChange={e => setTplForm({...tplForm, title: e.target.value})} className="h-9 text-sm" data-testid="admin-tpl-title" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-sans">{t('ct_admin_tpl_content')}</Label>
              <textarea
                value={tplForm.content}
                onChange={e => setTplForm({...tplForm, content: e.target.value})}
                rows={12}
                className="w-full text-xs font-mono p-3 border border-border rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Testo completo del contratto..."
                data-testid="admin-tpl-content"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={tplForm.activate} onChange={e => setTplForm({...tplForm, activate: e.target.checked})} data-testid="admin-tpl-activate" />
              <span className="text-sm font-sans">{t('ct_admin_tpl_activate')}</span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowTemplateDlg(false)} className="rounded-full">Annulla</Button>
            <Button onClick={saveTemplate} disabled={tplSaving} className="rounded-full" data-testid="admin-tpl-save-btn">
              {tplSaving ? 'Salvo...' : t('ct_admin_tpl_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
